import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Container, TextField, Button, Box, Typography, Divider, Alert} from '@mui/material';
import { API_ROOT } from './config';

function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");  // State for showing messages (success or error)
  const [severity, setSeverity] = useState("success");  // For controlling message style (success or error)
  const [timeoutDuration, setTimeoutDuration] = useState(5000);  // Default timeout duration (in milliseconds)
  const location = useLocation();

  useEffect(() => {

    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get('token');

    if (token) {
      localStorage.setItem('authToken', token);
      navigate('/');  // Redirect to the main page after login
    }
  }, [navigate]);

  // Effect to automatically clear the message after the specified timeout duration
  useEffect(() => {
    if (message) {
      const timeout = setTimeout(() => {
        setMessage("");  // Clear the message
      }, timeoutDuration);  // Dynamic timeout based on state

      return () => clearTimeout(timeout);  // Clear timeout if component unmounts or message changes
    }
  }, [message, timeoutDuration]);  // Run effect when 'message' or 'timeoutDuration' changes

  // Function to show messages with optional dynamic timeout and severity
  const showMessage = (msg, type = "success", duration = 5000) => {
    setMessage(msg);
    setSeverity(type);
    setTimeoutDuration(duration);  // Set dynamic timeout duration
  };

  useEffect(() => {
    if (location.state?.successMessage) {
      showMessage(location.state.successMessage, "success", 5000);
    }
  }, [location]);

  const handleUsernamePasswordLogin = async () => {
    if (!username || !password) {
      showMessage("Please fill in both username and password.", "warning", 5000);
      return;
    }
  
    try {
      const response = await fetch(`/oxbo-translation-api/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          username: username,
          password: password,
        }),
      });
  
      if (response.ok) {
        const data = await response.json();
        const token = data.access_token; // Assuming your backend returns a JWT token
  
        // Save the token in localStorage
        localStorage.setItem('authToken', token);
  
        // Redirect the user to the protected page or dashboard
        navigate('/'); // Adjust the route as needed
      } else {
        const errorData = await response.json();
        console.error("Login failed:", errorData);
        showMessage(errorData.detail || "Login failed. Please try again.", "error", 5000);
      }
    } catch (error) {
      console.error("Error during login:", error);
      showMessage("An error occurred. Please try again.", "error", 5000);
    }
  };
  const handleGithubLogin = async () => {
    try {
      const response = await fetch(`${API_ROOT}auth/github/login`);
      const data = await response.json();
      if (data.authorization_url) {
        window.location.href = data.authorization_url; // Redirect to GitHub OAuth URL
      }
    } catch (error) {
      console.error("Error fetching GitHub authorization URL:", error);
      showMessage("An error occurred while fetching the GitHub authorization URL.", "error", 5000);
    }
  };

  return (
    <Container maxWidth="xs">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Typography variant="h4" sx={{ mb: 3 }}>
          Login
        </Typography>

        {message && (
          <Alert severity={severity} sx={{ mb: 3 }}>
            {message}
          </Alert>
        )}

        {/* Username/Password form */}
        <TextField
          label="Username"
          variant="outlined"
          fullWidth
          sx={{ mb: 2 }}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <TextField
          label="Password"
          type="password"
          variant="outlined"
          fullWidth
          sx={{ mb: 2 }}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mb: 3 }}
          onClick={handleUsernamePasswordLogin}
        >
          Login with Username/Password
        </Button>

        {/* Divider with OR text */}
        <Divider sx={{ width: '100%', mb: 3 }}>OR</Divider>

        {/* GitHub OAuth Login */}
        <Button
          variant="outlined"
          fullWidth
          onClick={handleGithubLogin}
          sx={{ mb: 2 }}
        >
          Login with GitHub
        </Button>
        {/* Link to Register Page */}
        <Box sx={{ mt: 3 }}>
          <Typography>
            Don't have an account? <Link to="/register">Register here</Link>
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}

export default Login;
